<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { useI18n } from "vue-i18n"
import { computed, ref, watch } from "vue"

// Constant
import { USAGE_TYPES_KEY, USAGES_RULES } from "@core/constants"

// Service
import { SourceService } from "@core/services"
const sourceService = new SourceService()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Modifier les usages du compteur",
            button_validate: "Valider",
            button_quit: "Quitter",
            meter_name: "Nom du compteur",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    type: string | undefined
    usages: string[] | undefined
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const emit = defineEmits(["update"])

// ================================
// REACTIVE STATE
// ================================

const usagesSelected = ref<string[]>([])
const loading = ref(false)
const error = ref(null)

// ================================
// WATCHERS
// ================================
watch(
    () => props.usages,
    (newVal) => {
        if (!newVal) return
        usagesSelected.value = newVal || []
    },
    { immediate: true }
)

// ================================
// COMPUTED PROPERTIES
// ================================
const getUsageList = computed(() => {
    if (!props.type) return Object.values(USAGE_TYPES_KEY)

    const entry = Object.entries(USAGES_RULES).find(
        ([sourceType]) => sourceType === props.type
    )

    return entry?.[1] || []
})

// ================================
// METHODS
// ================================
const onSubmit = async () => {
    error.value = null
    loading.value = true
    // const body = { name: buildingNameModel }
    const body = {
        usages: usagesSelected.value,
    }
    try {
        await sourceService.updateMeterById(props.meterId, body)
        loading.value = false
        emit("update")
        visible.value = false
    } catch (e) {
        loading.value = false
        error.value = e
    }
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        position="top"
        class="modal-source-edit-usage w-3/4 md:w-1/2"
        :pt="{
            header: 'flex-col-reverse p-0!',
            headeractions: 'ml-auto',
            title: 'text-primary mb-4',
        }"
        :header="t('title')"
    >
        <div class="flex flex-col items-left w-full h-full">
            <div class="flex flex-col justify-center gap-4 my-2 pl-4">
                <div
                    v-for="(usage, index) in getUsageList"
                    :key="index"
                    class="flex items-center gap-2"
                >
                    <BaseCheckbox
                        v-model="usagesSelected"
                        input-id="ingredient1"
                        :name="usage"
                        :value="usage"
                    />
                    <label for="ingredient1"> {{ usage }} </label>
                </div>
            </div>
        </div>
        <template #footer>
            <div class="flex justify-between w-full">
                <BaseButton
                    @click="visible = false"
                    :label="t('button_quit')"
                    text
                    severity="secondary"
                />
                <BaseButton
                    @click="onSubmit"
                    :label="t('button_validate')"
                    severity="primary"
                    autofocus
                    :loading="loading"
                />
            </div>
        </template>
    </BaseDialog>
</template>
